import { ContactGroupDetailPage, ContactGroupsPage } from '@/pages/ContactGroupsPage'
import { EmailLogPage } from '@/pages/EmailLogPage'
import { BlastsPage } from '@/pages/BlastsPage'
import { BlastReportPage } from '@/pages/BlastReportPage'
import { SegmentBuilderPage } from '@/pages/SegmentBuilderPage'
import { ViewOnlyContext } from '@/components/templates/app-shell'

export type CommunicationView =
  | { kind: 'communication-email-log' }
  | { kind: 'communication-blasts' }
  | { kind: 'communication-blast-report'; blastId: number }
  | { kind: 'communication-audiences' }
  | { kind: 'communication-group-detail'; groupId: number }
  | { kind: 'communication-segment-new' }
  | { kind: 'communication-segment-detail'; segmentId: number }

export function isCommunicationView(view: { kind: string }): view is CommunicationView {
  return view.kind.startsWith('communication-')
}

export function StaffCommunicationView({
  view,
  navigateTo,
  segmentManage = false,
  groupManage = false,
}: {
  view: CommunicationView
  navigateTo: (view: CommunicationView, path: string) => void
  segmentManage?: boolean
  /** The API's `groups` manage capability: Administrator and Membership Manager write Groups. */
  groupManage?: boolean
}) {
  const showAudiences = () =>
    navigateTo({ kind: 'communication-audiences' }, '/communication/audiences')

  if (view.kind === 'communication-email-log') return <EmailLogPage />
  if (view.kind === 'communication-blasts')
    return (
      <BlastsPage
        onOpenReport={(blastId) =>
          navigateTo(
            { kind: 'communication-blast-report', blastId },
            `/communication/blasts/${blastId}`,
          )
        }
      />
    )
  if (view.kind === 'communication-blast-report')
    return (
      <BlastReportPage
        blastId={view.blastId}
        onBack={() => navigateTo({ kind: 'communication-blasts' }, '/communication/blasts')}
      />
    )
  if (view.kind === 'communication-group-detail') {
    return (
      <ViewOnlyContext.Provider value={!groupManage}>
        <ContactGroupDetailPage
          groupId={view.groupId}
          onBack={showAudiences}
          onDeleted={showAudiences}
        />
      </ViewOnlyContext.Provider>
    )
  }
  if (view.kind === 'communication-segment-new' || view.kind === 'communication-segment-detail') {
    return (
      <ViewOnlyContext.Provider value={!segmentManage}>
        <SegmentBuilderPage
          segmentId={view.kind === 'communication-segment-detail' ? view.segmentId : undefined}
          onBack={showAudiences}
          onSaved={(segment) =>
            navigateTo(
              { kind: 'communication-segment-detail', segmentId: segment.id },
              `/communication/audiences/segments/${segment.id}`,
            )
          }
        />
      </ViewOnlyContext.Provider>
    )
  }

  // The list's Create Segment primary action follows the API's `segments` manage
  // capability, like the builder above; a Membership Manager manages Segments without
  // managing `communications`. Create Group follows `groupManage` on its own.
  return (
    <ViewOnlyContext.Provider value={!segmentManage}>
      <ContactGroupsPage
        groupManage={groupManage}
        onOpen={(group) =>
          navigateTo(
            { kind: 'communication-group-detail', groupId: group.id },
            `/communication/audiences/${group.id}`,
          )
        }
        onCreateSegment={() =>
          navigateTo({ kind: 'communication-segment-new' }, '/communication/audiences/segments/new')
        }
        onOpenSegment={(segment) =>
          navigateTo(
            { kind: 'communication-segment-detail', segmentId: segment.id },
            `/communication/audiences/segments/${segment.id}`,
          )
        }
      />
    </ViewOnlyContext.Provider>
  )
}
